/**
 * Outils du document riche (corps d'article), partagés par l'API, l'éditeur
 * et le site : conversion de l'ancien format, texte brut, aperçu, vidéos.
 */
export const emptyDoc = () => ({ type: 'doc', content: [{ type: 'paragraph' }] });
const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)\s]+\))/g;
/** Texte balisé de l'ancien format (**gras**, *italique*, [lien](url)) → nœuds en ligne. */
export function inlineFromMarkup(text) {
    const out = [];
    const push = (t, marks) => {
        t.split('\n').forEach((line, i) => {
            if (i > 0)
                out.push({ type: 'hardBreak' });
            if (line)
                out.push(marks ? { type: 'text', text: line, marks } : { type: 'text', text: line });
        });
    };
    for (const part of text.split(INLINE)) {
        if (!part)
            continue;
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4)
            push(part.slice(2, -2), [{ type: 'bold' }]);
        else if (part.startsWith('*') && part.endsWith('*') && part.length > 2)
            push(part.slice(1, -1), [{ type: 'italic' }]);
        else {
            const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
            if (link)
                push(link[1], [{ type: 'link', attrs: { href: link[2] } }]);
            else
                push(part);
        }
    }
    return out;
}
const paragraph = (text) => {
    const content = inlineFromMarkup(text.trim());
    return content.length ? { type: 'paragraph', content } : { type: 'paragraph' };
};
/** Document fait de paragraphes (ligne vide = nouveau paragraphe). */
export function docFromText(text) {
    const paras = text.split(/\n\s*\n/).filter((p) => p.trim());
    return { type: 'doc', content: paras.length ? paras.map(paragraph) : [{ type: 'paragraph' }] };
}
/** Ancien format → document. */
export function legacyToDoc(nodes) {
    const content = [];
    for (const n of nodes) {
        switch (n.type) {
            case 'p':
                content.push(...docFromText(n.text).content);
                break;
            case 'h2':
                content.push({
                    type: 'heading',
                    attrs: { level: 2 },
                    content: n.text ? [{ type: 'text', text: n.text }] : [],
                });
                break;
            case 'quote':
                content.push({
                    type: 'blockquote',
                    content: [
                        paragraph(n.text),
                        ...(n.cite
                            ? [
                                {
                                    type: 'paragraph',
                                    content: [
                                        {
                                            type: 'text',
                                            text: `— ${n.cite}`,
                                            marks: [{ type: 'italic' }],
                                        },
                                    ],
                                },
                            ]
                            : []),
                    ],
                });
                break;
            case 'image':
                if (n.image)
                    content.push({
                        type: 'figure',
                        attrs: { src: n.image.url, alt: n.image.alt, caption: n.caption, size: 'normal' },
                    });
                break;
            case 'gallery':
                content.push({ type: 'gallery', attrs: { images: n.images, caption: n.caption } });
                break;
            case 'list': {
                const items = n.items
                    .split('\n')
                    .map((l) => l.trim())
                    .filter(Boolean);
                if (items.length)
                    content.push({
                        type: n.ordered ? 'orderedList' : 'bulletList',
                        content: items.map((l) => ({ type: 'listItem', content: [paragraph(l)] })),
                    });
                break;
            }
        }
    }
    return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}
/** N'importe quel corps stocké (document ou ancien format) → document. */
export function toDoc(body) {
    if (Array.isArray(body))
        return legacyToDoc(body);
    if (body && typeof body === 'object' && body.type === 'doc') {
        const doc = body;
        return { type: 'doc', content: Array.isArray(doc.content) ? doc.content : [] };
    }
    return emptyDoc();
}
/** Texte brut du document (temps de lecture, recherche, lettre d'information). */
export function docText(doc) {
    const parts = [];
    const walk = (nodes) => {
        for (const n of nodes ?? []) {
            if (n.type === 'text')
                parts.push(n.text);
            else {
                if (n.type === 'hardBreak')
                    parts.push(' ');
                if (n.type === 'figure' && n.attrs?.caption)
                    parts.push(n.attrs.caption);
                walk(n.content);
                if (n.type === 'paragraph' || n.type === 'heading')
                    parts.push('\n');
            }
        }
    };
    walk(doc.content);
    return parts
        .join('')
        .replace(/[ \t]+/g, ' ')
        .trim();
}
export function readingMinutes(doc) {
    const words = docText(doc).split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 220));
}
/**
 * Début d'un article réservé aux abonnés : les premiers paragraphes, jusqu'à
 * environ `words` mots (et au plus un tiers du texte) — de quoi donner envie,
 * pas de quoi tout lire.
 */
export function docPreview(doc, words = 90) {
    const content = [];
    // Jamais plus du tiers de l'article : un texte court ne doit pas être livré presque entier.
    const total = docText(doc).split(/\s+/).filter(Boolean).length;
    const limit = Math.min(words, Math.max(20, Math.round(total / 3)));
    let count = 0;
    for (const n of doc.content) {
        if (count >= limit)
            break;
        if (n.type === 'figure' || n.type === 'gallery' || n.type === 'embed')
            continue;
        content.push(n);
        count += docText({ type: 'doc', content: [n] })
            .split(/\s+/)
            .filter(Boolean).length;
    }
    return { type: 'doc', content };
}
/**
 * Vidéo intégrable : adresse de la page (YouTube, Vimeo, Dailymotion) →
 * adresse du lecteur. Toute autre adresse est refusée : on n'intègre jamais
 * une page arbitraire dans le site.
 */
export function embedSrc(url) {
    let u;
    try {
        u = new URL(url.trim());
    }
    catch {
        return null;
    }
    if (u.protocol !== 'https:')
        return null;
    const host = u.hostname.replace(/^www\.|^m\./, '');
    const id = (v) => (v && /^[\w-]{4,40}$/.test(v) ? v : null);
    if (host === 'youtube.com') {
        const v = id(u.searchParams.get('v')) ?? id(/^\/(?:shorts|embed|live)\/([\w-]+)/.exec(u.pathname)?.[1]);
        return v ? `https://www.youtube-nocookie.com/embed/${v}` : null;
    }
    if (host === 'youtu.be') {
        const v = id(u.pathname.slice(1));
        return v ? `https://www.youtube-nocookie.com/embed/${v}` : null;
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
        const v = /(\d{5,12})/.exec(u.pathname)?.[1];
        return v ? `https://player.vimeo.com/video/${v}?dnt=1` : null;
    }
    if (host === 'dailymotion.com' || host === 'dai.ly') {
        const v = id(host === 'dai.ly' ? u.pathname.slice(1) : /\/video\/([\w]+)/.exec(u.pathname)?.[1]);
        return v ? `https://www.dailymotion.com/embed/video/${v}` : null;
    }
    return null;
}
//# sourceMappingURL=doc.js.map